<!-- The phone mounting of CropSurface.
  --
  -- On a full-sized screen the surface sits inline in the stage, where the
  -- tools can be on the picture and the settings stay visible beside it. A
  -- phone column is too narrow to drag a rectangle in, so there it takes the
  -- whole screen instead. Same component either way, so the two cannot drift.
  -->
<template>
    <div v-if="open" class="fixed inset-0 z-50 flex flex-col bg-black/90 p-3" role="dialog" aria-modal="true" :aria-label="t('crop.region')">
        <div class="flex items-center justify-between gap-4 py-2 text-white">
            <span class="text-[15px] font-bold">{{ t('crop.dragRegion') }}</span>
            <button @click.prevent="$emit('close')" class="btn btn-sm btn-ghost rounded-lg text-white">{{ t('crop.done') }}</button>
        </div>
        <div class="flex-1 min-h-0 overflow-auto">
            <CropSurface ref="surface"
                         :file="file" :crop="crop" :flip-h="flipH" :flip-v="flipV"
                         :min-height="minHeight"
                         @apply-crop="onApply"
                         @flip-h="$emit('flip-h')"
                         @flip-v="$emit('flip-v')"
                         @reset="$emit('reset')" />
        </div>
    </div>
</template>

<script setup>
import { ref, watch, nextTick } from "vue"
import CropSurface from "./CropSurface.vue"
import { useI18n } from 'vue-i18n'

const props = defineProps({
    open: {type: Boolean, default: false},
    file: {type: Object, default: null},
    crop: {type: Object, default: null},
    flipH: {type: Boolean, default: false},
    flipV: {type: Boolean, default: false},
    minHeight: {type: Number, default: 380},
})

const { t } = useI18n()
const emit = defineEmits(["close", "apply", "flip-h", "flip-v", "reset"])

const surface = ref(null)

// Opening this dialog IS asking to crop, so the rectangle is up straight away
// rather than after a second tap on a tool that is already the reason you are
// looking at this screen.
watch(() => props.open, async (isOpen) => {
    if (!isOpen) return
    await nextTick()
    if (surface.value) surface.value.openCrop()
})

function onApply(region) {
    emit("apply", region)
    emit("close")
}
</script>
